"use client";

import { createContext, useContext, useMemo, type ReactNode } from "react";
import { useAuthStore } from "@/stores/authStore";
import type { User } from "@/lib/types/user";

type AuthContextValue = {
  user: User | null;
  accessToken: string | null;
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  refreshToken: () => Promise<void>;
};

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const user = useAuthStore((s) => s.user);
  const accessToken = useAuthStore((s) => s.accessToken);
  const login = useAuthStore((s) => s.login);
  const logout = useAuthStore((s) => s.logout);
  const refreshTokenFn = useAuthStore((s) => s.refreshTokenFn);

  const value = useMemo(
    () => ({
      user,
      accessToken,
      login,
      logout,
      refreshToken: refreshTokenFn,
    }),
    [user, accessToken, login, logout, refreshTokenFn],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error("useAuth must be used within AuthProvider");
  }

  return ctx;
}
